import { IonIcon } from "@ionic/react";
import { closeCircleOutline, stop } from "ionicons/icons";
import React, {
  useRef,
  useState,
  useEffect,
  FC,
  RefObject,
  MutableRefObject,
} from "react";

interface Props {
  /** Called with the final video blob, its URL, and duration (s) when recording stops */
  onRecordingComplete: (blob: Blob, url: string, duration: number) => void;
  /** Called when the user closes the preview before recording */
  onClose?: () => void;
  /** Max recording length in milliseconds; if omitted, no auto-stop */
  maxDuration?: number;
}

const VideoRecorder: FC<Props> = ({
  onRecordingComplete,
  maxDuration,
  onClose,
}) => {
  // Detect iOS Safari for fallback
  const fallbackFileMode = /iP(hone|ad|od).*Version\/.*Safari/.test(
    navigator.userAgent,
  );

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef: RefObject<HTMLVideoElement> = useRef(null);
  const mediaRecorderRef: MutableRefObject<MediaRecorder | null> = useRef(null);
  const recordedChunksRef: MutableRefObject<Blob[]> = useRef([]);
  const recordIntervalRef: MutableRefObject<number | null> = useRef(null);
  const preIntervalRef: MutableRefObject<number | null> = useRef(null);
  const recordingActiveRef: MutableRefObject<boolean> = useRef(false);
  const recordStartTimeRef: MutableRefObject<number | null> = useRef(null);

  // State
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [preCount, setPreCount] = useState<number | null>(null);

  // Trigger file input only on user tap (not on mount)
  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  // Handle video file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      onClose?.();
      return;
    }
    const url = URL.createObjectURL(file);
    const vid = document.createElement("video");
    vid.preload = "metadata";
    vid.onloadedmetadata = () => {
      const duration = vid.duration;
      URL.revokeObjectURL(vid.src);
      onRecordingComplete(file, url, duration);
    };
    vid.src = url;
  };

  // Acquire camera stream (skip if fallback mode)
  useEffect(() => {
    if (fallbackFileMode) return;

    let mounted = true;
    (async () => {
      const supported = navigator.mediaDevices.getSupportedConstraints();
      const videoConstraints: MediaTrackConstraints = { facingMode: "user" };
      if (supported.width) videoConstraints.width = { ideal: 1080, min: 720 };
      if (supported.height)
        videoConstraints.height = { ideal: 1920, min: 1280 };
      if (supported.aspectRatio) videoConstraints.aspectRatio = 9 / 16;

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: true,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
      }
      if (!mounted) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      if (videoRef.current) videoRef.current.srcObject = stream;
      setMediaStream(stream);
    })();

    return () => {
      mounted = false;
      if (mediaStream) {
        mediaStream.getTracks().forEach((t) => t.stop());
      }
      if (recordIntervalRef.current !== null)
        clearInterval(recordIntervalRef.current);
      if (preIntervalRef.current !== null)
        clearInterval(preIntervalRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Format seconds to MM:SS
  const formatRemaining = (tot: number) => {
    const m = String(Math.floor(tot / 60)).padStart(2, "0");
    const s = String(tot % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  // Close preview
  const handleClose = () => {
    recordingActiveRef.current = false;
    if (mediaStream) {
      mediaStream.getTracks().forEach((t) => t.stop());
    }
    if (recordIntervalRef.current !== null) {
      clearInterval(recordIntervalRef.current);
      recordIntervalRef.current = null;
    }
    if (preIntervalRef.current !== null) {
      clearInterval(preIntervalRef.current);
      preIntervalRef.current = null;
    }
    setMediaStream(null);
    setPreCount(null);
    setTimeLeft(null);
    onClose?.();
  };

  // Start MediaRecorder + max-duration countdown
  const startRecording = () => {
    recordStartTimeRef.current = Date.now();

    if (maxDuration && maxDuration > 0) {
      if (recordIntervalRef.current !== null)
        clearInterval(recordIntervalRef.current);
      const endTime = Date.now() + maxDuration;
      setTimeLeft(Math.ceil(maxDuration / 1000));
      recordIntervalRef.current = window.setInterval(() => {
        const secsLeft = Math.ceil((endTime - Date.now()) / 1000);
        if (secsLeft <= 0) {
          clearInterval(recordIntervalRef.current!);
          recordIntervalRef.current = null;
          handleStop();
        } else {
          setTimeLeft(secsLeft);
        }
      }, 1000);
    }

    const mimeCandidates = [
      "video/mp4;codecs=avc1",
      "video/mp4",
      "video/webm;codecs=vp8",
      "video/webm",
    ];
    const mimeType = mimeCandidates.find((t) =>
      MediaRecorder.isTypeSupported(t),
    );
    const recorder = mimeType
      ? new MediaRecorder(mediaStream!, { mimeType })
      : new MediaRecorder(mediaStream!);

    recordingActiveRef.current = true;
    recordedChunksRef.current = [];

    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data.size) recordedChunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      if (recordingActiveRef.current) {
        const blob = new Blob(recordedChunksRef.current, {
          type: mimeType ?? "video/mp4",
        });
        const url = URL.createObjectURL(blob);
        const durMs =
          recordStartTimeRef.current != null
            ? Date.now() - recordStartTimeRef.current
            : 0;
        onRecordingComplete(blob, url, durMs / 1000);
        recordingActiveRef.current = false;
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach((t) => t.stop());
      }
      setMediaStream(null);
      setIsRecording(false);
      setTimeLeft(null);
    };

    mediaRecorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
  };

  // Pre-count then record
  const handleStart = () => {
    if (!mediaStream) return;
    setPreCount(3);
    preIntervalRef.current = window.setInterval(() => {
      setPreCount((prev) => {
        if (!prev || prev <= 1) {
          clearInterval(preIntervalRef.current!);
          preIntervalRef.current = null;
          setPreCount(null);
          startRecording();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Stop recording or cancel pre-count
  const handleStop = () => {
    if (preIntervalRef.current !== null) {
      clearInterval(preIntervalRef.current);
      preIntervalRef.current = null;
      setPreCount(null);
      return;
    }
    if (recordIntervalRef.current !== null) {
      clearInterval(recordIntervalRef.current);
      recordIntervalRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  };

  return (
    <>
      {fallbackFileMode ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            capture="user"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <div
            onClick={handleFileClick}
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#000",
              color: "#fff",
              fontSize: "1.25rem",
              cursor: "pointer",
            }}
          >
            Tap to Record Video
          </div>
        </>
      ) : (
        <div style={{ width: "100%", position: "relative" }}>
          {/* 9:16 preview container */}
          <div
            style={{
              position: "relative",
              width: "100%",
              paddingTop: "177.78%",
              background: "#000",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            {/* Countdown */}
            {preCount != null && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "#fff",
                  fontSize: "4rem",
                  fontWeight: "bold",
                }}
              >
                {preCount}
              </div>
            )}

            {/* Timer */}
            {isRecording && timeLeft != null && (
              <div
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  background: "rgba(255,255,255,0.8)",
                  color: "#000",
                  padding: "4px 8px",
                  borderRadius: 4,
                  fontWeight: "bold",
                }}
              >
                {formatRemaining(timeLeft)}
              </div>
            )}

            {/* Close */}
            {!isRecording && preCount == null && onClose && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  background: "rgba(0,0,0,0.4)",
                  borderRadius: "50%",
                  width: 48,
                  height: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <IonIcon
                  icon={closeCircleOutline}
                  style={{ fontSize: 32, color: "#fff" }}
                />
              </div>
            )}
          </div>

          {/* Controls */}
          <div
            style={{
              position: "absolute",
              bottom: "2rem",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              onClick={() => {
                if (preCount != null) {
                  clearInterval(preIntervalRef.current!);
                  preIntervalRef.current = null;
                  setPreCount(null);
                  return;
                }
                isRecording ? handleStop() : handleStart();
              }}
            >
              {isRecording ? (
                <div className="h-[70px] w-[70px] border-[5px] border-red-500 rounded-full flex justify-center items-center">
                  <IonIcon icon={stop} style={{ fontSize: 40, color: "red" }} />
                </div>
              ) : preCount != null ? null : (
                <div className="h-[70px] w-[70px] bg-white rounded-full flex justify-center items-center">
                  <div className="bg-red-500 rounded-full h-[60px] w-[60px]" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoRecorder;
